import React from 'react';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { Send, Instagram, ChevronRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import {
  heroData,
  approachData,
  situations,
  situationsClosing,
  aboutData,
  statsData,
  resultBlock,
  afterPricingBlock,
  faqData,
  benefitsData,
  pricing90,
  pricing60,
  testimonials,
  contactInfo
} from '../mockData';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
  }
};

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 }
  }
};

const slideInLeft = {
  hidden: { opacity: 0, x: -40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: 'easeOut' } }
};

const slideInRight = {
  hidden: { opacity: 0, x: 40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: 'easeOut' } }
};

const PricingCard = ({ plan, variant = 'gold' }) => (
  <div
    className="card-subtle rounded-2xl p-6 md:p-8 flex flex-col h-full"
    data-testid={`pricing-card-${plan.id}`}
  >
    <p className="text-xs uppercase tracking-[0.15em] text-muted mb-2">{plan.duration}</p>
    <h4 className="font-serif text-2xl lg:text-3xl text-foreground mb-4">{plan.name}</h4>
    <div className="flex items-baseline gap-1 mb-6 mt-auto">
      <span className="font-serif text-4xl lg:text-5xl text-foreground">{plan.price}</span>
      <span className="text-xl text-muted">{plan.currency}</span>
    </div>
    <a
      href={plan.paymentUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center justify-center w-full py-3.5 rounded-full text-sm tracking-wide ${
        variant === 'gold' ? 'btn-gold' : 'btn-purple'
      }`}
      data-testid={`pay-btn-${plan.id}`}
    >
      Оплатить
    </a>
  </div>
);

const LandingPage = () => {
  return (
    <div className="min-h-screen overflow-x-hidden">

      {/* ── Hero ── */}
      <section data-testid="hero-section" className="pt-16 sm:pt-20 lg:pt-24 pb-12 lg:pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="text-center space-y-3 mb-10 lg:mb-14"
          >
            <h1
              className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tight leading-[1.05]"
              data-testid="hero-name"
            >
              {heroData.name}
            </h1>
            <p className="text-sm sm:text-base uppercase tracking-[0.18em] text-foreground/70">
              {heroData.subtitle}
            </p>
            <p className="text-base sm:text-lg text-muted">
              {heroData.specialization}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.3, ease: 'easeOut' }}
            className="flex justify-center mb-10 lg:mb-14"
          >
            <div className="w-full max-w-xl rounded-2xl lg:rounded-[2rem] overflow-hidden aspect-[4/3]">
              <img
                src={heroData.imageUrl}
                alt={heroData.name}
                className="w-full h-full object-cover"
                style={{ objectPosition: 'center 15%' }}
                data-testid="hero-photo"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <a
              href={contactInfo.telegramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-sm tracking-wide"
              data-testid="hero-primary-cta"
            >
              Записаться на консультацию
              <ChevronRight className="w-4 h-4" />
            </a>
            <a
              href="#pricing"
              className="btn-outline inline-flex items-center justify-center px-8 py-4 rounded-full text-sm tracking-wide"
              data-testid="hero-secondary-cta"
            >
              Посмотреть форматы работы
            </a>
          </motion.div>
        </div>
      </section>

      <div className="divider max-w-5xl mx-auto" />

      {/* ── Approach ── */}
      <section data-testid="approach-section" className="py-24 lg:py-36">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            variants={stagger}
            className="space-y-6 text-center"
          >
            {approachData.lines.map((line, i) => (
              <motion.p
                key={i}
                variants={fadeInUp}
                className={
                  i === 0
                    ? 'font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight italic'
                    : 'text-base lg:text-lg text-muted leading-relaxed'
                }
              >
                {line}
              </motion.p>
            ))}
          </motion.div>
        </div>
      </section>

      <div className="divider max-w-5xl mx-auto" />

      {/* ── Situations ── */}
      <section data-testid="situations-section" className="py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={stagger}
          >
            <motion.h2
              variants={fadeInUp}
              className="font-serif text-3xl sm:text-4xl lg:text-5xl text-center mb-20 leading-tight"
            >
              В каких ситуациях я могу вам помочь
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-16">
              {situations.map((item) => (
                <motion.div
                  key={item.number}
                  variants={fadeInUp}
                  className="group"
                  data-testid={`situation-${item.number}`}
                >
                  <div className="flex items-start gap-5">
                    <span className="situation-number select-none">{item.number}</span>
                    <div className="pt-3 space-y-3 flex-1">
                      <h3 className="font-serif text-2xl lg:text-3xl text-foreground group-hover:text-accent transition-colors duration-300">
                        {item.title}
                      </h3>
                      <div className="space-y-3">
                        {item.text.split('\n\n').map((paragraph, i) => (
                          <p key={i} className="text-base text-muted leading-relaxed">{paragraph}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.p
              variants={fadeInUp}
              className="text-center text-lg lg:text-xl text-accent mt-20 font-serif italic"
            >
              {situationsClosing}
            </motion.p>

            <motion.div variants={fadeInUp} className="flex justify-center mt-10">
              <a
                href={contactInfo.telegramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-sm tracking-wide"
                data-testid="situations-cta"
              >
                Записаться на консультацию
                <ChevronRight className="w-4 h-4" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <div className="divider max-w-5xl mx-auto" />

      {/* ── About ── */}
      <section data-testid="about-section" className="py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={stagger}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center"
          >
            <motion.div variants={slideInLeft} className="flex justify-center">
              <div className="w-full max-w-md aspect-[3/4] rounded-2xl lg:rounded-[2rem] overflow-hidden shadow-lg">
                <img
                  src={aboutData.imageUrl}
                  alt={aboutData.name}
                  className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-700"
                  data-testid="about-photo"
                />
              </div>
            </motion.div>

            <motion.div variants={slideInRight} className="space-y-6">
              <p className="text-sm uppercase tracking-[0.2em] text-accent">Обо мне</p>
              <h2 className="font-serif text-4xl lg:text-5xl leading-tight">
                {aboutData.name}
              </h2>
              <p className="text-base lg:text-lg text-foreground/70">
                {aboutData.role}
              </p>
              <div className="space-y-4">
                {aboutData.paragraphs.map((p, i) => (
                  <p
                    key={i}
                    className={`text-base lg:text-lg leading-relaxed whitespace-pre-line ${
                      p.bold ? 'text-foreground font-medium' : 'text-muted'
                    }`}
                  >
                    {p.text}
                  </p>
                ))}
              </div>
              <div className="pt-4">
                <a
                  href="#pricing"
                  className="btn-outline inline-flex items-center justify-center px-8 py-4 rounded-full text-sm tracking-wide"
                  data-testid="about-cta"
                >
                  Посмотреть форматы работы
                </a>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section data-testid="stats-section" className="py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="divider mb-16" />
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
            variants={stagger}
            className="grid grid-cols-1 sm:grid-cols-3 gap-12 text-center"
          >
            {statsData.map((stat, i) => (
              <motion.div key={i} variants={fadeInUp} data-testid={`stat-${i}`}>
                <p className="font-serif text-4xl sm:text-5xl lg:text-6xl text-accent mb-3">
                  {stat.number}
                </p>
                <p className="text-sm text-muted leading-relaxed max-w-[200px] mx-auto">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </motion.div>
          <div className="divider mt-16" />
        </div>
      </section>

      {/* ── Benefits ── */}
      <section data-testid="benefits-section" className="py-24 lg:py-36">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={stagger}
          >
            <motion.div variants={fadeInUp} className="text-center mb-20 space-y-4">
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">
                {benefitsData.title}
              </h2>
              <p className="text-base lg:text-lg text-muted">
                {benefitsData.subtitle}
              </p>
            </motion.div>

            <div className="space-y-10">
              {benefitsData.items.map((item) => (
                <motion.div
                  key={item.number}
                  variants={fadeInUp}
                  className="flex items-start gap-6 group"
                  data-testid={`benefit-${item.number}`}
                >
                  <span className="font-serif text-4xl lg:text-5xl text-accent/25 group-hover:text-accent/50 transition-colors duration-300 leading-none select-none shrink-0">
                    {item.number}
                  </span>
                  <p className="text-base lg:text-lg text-foreground/80 leading-relaxed pt-2">
                    {item.text}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <div className="divider max-w-5xl mx-auto" />
      <section data-testid="result-section" className="py-24 lg:py-36">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            variants={stagger}
            className="space-y-6 text-center"
          >
            <motion.h2
              variants={fadeInUp}
              className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight"
            >
              {resultBlock.title}
            </motion.h2>
            {resultBlock.lines.map((line, i) => (
              <motion.p
                key={i}
                variants={fadeInUp}
                className="text-base lg:text-lg text-muted leading-relaxed"
              >
                {line}
              </motion.p>
            ))}
          </motion.div>
        </div>
      </section>

      <div className="divider max-w-5xl mx-auto" />

      {/* ── Pricing ── */}
      <section id="pricing" data-testid="pricing-section" className="py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={stagger}
          >
            <motion.div variants={fadeInUp} className="text-center mb-20 space-y-4">
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">
                Форматы работы со мной
              </h2>
              <p className="text-base lg:text-lg text-muted max-w-2xl mx-auto">
                Вы можете выбрать формат, который подходит именно вам.
              </p>
            </motion.div>

            {/* 60 min tier */}
            <motion.div variants={fadeInUp} className="mb-20">
              <div className="flex items-center gap-4 mb-8">
                <h3 className="font-serif text-2xl lg:text-3xl whitespace-nowrap">60 минут</h3>
                <div className="flex-1 divider" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {pricing60.map((plan) => (
                  <motion.div key={plan.id} variants={fadeInUp}>
                    <PricingCard plan={plan} variant="purple" />
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* 90 min tier */}
            <motion.div variants={fadeInUp}>
              <div className="flex items-center gap-4 mb-8">
                <h3 className="font-serif text-2xl lg:text-3xl whitespace-nowrap">90 минут</h3>
                <div className="flex-1 divider" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {pricing90.map((plan) => (
                  <motion.div key={plan.id} variants={fadeInUp}>
                    <PricingCard plan={plan} variant="gold" />
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* After pricing block */}
            <motion.div
              variants={fadeInUp}
              className="max-w-2xl mx-auto mt-20 space-y-4 text-center"
            >
              {afterPricingBlock.lines.map((line, i) => (
                <p key={i} className="text-base lg:text-lg text-muted leading-relaxed">{line}</p>
              ))}
              <div className="pt-6">
                <a
                  href={contactInfo.telegramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-sm tracking-wide"
                  data-testid="after-pricing-cta"
                >
                  <Send className="w-4 h-4" />
                  Связаться со мной
                </a>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <div className="divider max-w-5xl mx-auto" />

      {/* ── Testimonials ── */}
      <section data-testid="testimonials-section" className="py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={stagger}
          >
            <motion.div variants={fadeInUp} className="text-center mb-16 space-y-4">
              <p className="text-sm uppercase tracking-[0.2em] text-accent">Отзывы</p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">
                Мои клиенты, которые уже прошли этот путь
              </h2>
              <p className="text-base lg:text-lg text-muted">
                Каждая история — о возвращении к себе.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <Swiper
                modules={[Navigation, Pagination]}
                slidesPerView={1}
                spaceBetween={24}
                navigation
                pagination={{ clickable: true }}
                breakpoints={{
                  640: { slidesPerView: 1 },
                  768: { slidesPerView: 2 },
                  1024: { slidesPerView: 3 }
                }}
                className="testimonials-swiper pb-14"
              >
                {testimonials.map((t) => (
                  <SwiperSlide key={t.id}>
                    <div
                      className="rounded-2xl overflow-hidden card-subtle"
                      data-testid={`testimonial-${t.id}`}
                    >
                      <img
                        src={t.imageUrl}
                        alt={`Отзыв ${t.id}`}
                        className="w-full h-auto object-contain"
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <div className="divider max-w-5xl mx-auto" />

      {/* ── FAQ ── */}
      <section data-testid="faq-section" className="py-24 lg:py-36">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={stagger}
          >
            <motion.h2
              variants={fadeInUp}
              className="font-serif text-3xl sm:text-4xl lg:text-5xl text-center mb-16 leading-tight"
            >
              Часто задаваемые вопросы
            </motion.h2>

            <motion.div variants={fadeInUp}>
              <Accordion type="single" collapsible className="space-y-3">
                {faqData.map((item, i) => (
                  <AccordionItem
                    key={i}
                    value={`faq-${i}`}
                    className="card-subtle rounded-2xl px-6 border-none"
                    data-testid={`faq-item-${i}`}
                  >
                    <AccordionTrigger className="text-base lg:text-lg text-foreground font-medium hover:no-underline py-5">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-base text-muted leading-relaxed pb-5 whitespace-pre-line">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── CTA + Footer ── */}
      <section data-testid="footer-section" className="section-deep py-24 lg:py-36">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={stagger}
            className="text-center space-y-10"
          >
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-5 justify-center">
              <a
                href={contactInfo.telegramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold inline-flex items-center justify-center gap-3 px-10 py-5 rounded-full text-base tracking-wide"
                data-testid="footer-telegram-btn"
              >
                <Send className="w-5 h-5" />
                Связаться со мной
              </a>
            </motion.div>

            <motion.div variants={fadeInUp} className="pt-8 space-y-6">
              <p className="text-sm opacity-60">
                Остались вопросы? Вы можете связаться со мной лично
              </p>
              <div className="flex justify-center gap-6">
                <a
                  href={contactInfo.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="opacity-50 hover:opacity-100 transition-opacity duration-300"
                  data-testid="footer-instagram-link"
                >
                  <Instagram className="w-6 h-6" strokeWidth={1.5} />
                </a>
                <a
                  href={contactInfo.telegramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="opacity-50 hover:opacity-100 transition-opacity duration-300"
                  data-testid="footer-telegram-link"
                >
                  <Send className="w-6 h-6" strokeWidth={1.5} />
                </a>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <div className="h-px bg-white/10 mt-12 mb-8" />
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm opacity-40">
                <p>{heroData.name}</p>
                <p>{contactInfo.website}</p>
                <p>&copy; {new Date().getFullYear()}</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
